import { useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Spinner, Surface, PageHeader, EmptyState } from "@/components/common";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Search, Inbox, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "./parts";

const STATUS = { ny: "Ny", kontaktet: "Kontaktet", moede_booket: "Møde booket", afsluttet: "Afsluttet" };
const STATUS_VARIANT = { ny: "default", kontaktet: "secondary", moede_booket: "secondary", afsluttet: "outline" };

export default function AdminLeads() {
  const [leads, setLeads] = useState(null);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState("");
  const [statusF, setStatusF] = useState("all");
  const [open, setOpen] = useState(null);
  const [note, setNote] = useState("");
  const [status, setStatus] = useState("ny");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/leads").then(({ data }) => setLeads(data.leads || [])).catch((e) => setErr(apiError(e)));
  useEffect(() => { load(); }, []);

  const rows = useMemo(() => {
    if (!leads) return [];
    let list = [...leads].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    if (statusF !== "all") list = list.filter((l) => (l.status || "ny") === statusF);
    if (q.trim()) { const s = q.toLowerCase(); list = list.filter((l) => [l.name, l.email, l.phone, l.source_page].some((v) => (v || "").toLowerCase().includes(s))); }
    return list;
  }, [leads, q, statusF]);

  const openLead = (l) => { setOpen(l); setNote(l.note || ""); setStatus(l.status || "ny"); };
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/leads/${open.id}`, { status, note });
      setLeads((prev) => prev.map((x) => (x.id === data.id ? data : x)));
      toast.success("Henvendelse opdateret"); setOpen(null);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const quickStatus = async (l, s) => {
    try { const { data } = await api.patch(`/admin/leads/${l.id}`, { status: s }); setLeads((prev) => prev.map((x) => (x.id === data.id ? data : x))); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (err) return <div className="py-16 text-center text-muted-foreground">{err}</div>;
  if (!leads) return <Spinner label="Henter henvendelser..." />;

  return (
    <div>
      <PageHeader overline="Administrator" title="Henvendelser" subtitle="Se og administrér henvendelser fra Book møde." />
      <div className="flex flex-wrap items-center gap-2 mb-5">
        <div className="relative flex-1 min-w-[220px]"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Søg navn, e-mail, telefon" className="pl-9 h-9" data-testid="leads-search" /></div>
        <Select value={statusF} onValueChange={setStatusF}><SelectTrigger className="w-[150px] h-9" data-testid="leads-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Alle statusser</SelectItem>{Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select>
      </div>

      <Surface testid="admin-leads">
        {rows.length === 0 ? <EmptyState icon={Inbox} title="Ingen henvendelser endnu" subtitle="Henvendelser fra Book møde vises her." /> : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Navn</TableHead><TableHead>E-mail</TableHead><TableHead>Telefon</TableHead>
                <TableHead>Kilde</TableHead><TableHead>Oprettet</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Handling</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((l) => (
                  <TableRow key={l.id} data-testid={`admin-lead-${l.id}`}>
                    <TableCell className="font-medium">{l.name}</TableCell>
                    <TableCell className="text-muted-foreground"><a href={`mailto:${l.email}`} className="hover:text-foreground">{l.email}</a></TableCell>
                    <TableCell className="text-muted-foreground">{l.phone || "—"}</TableCell>
                    <TableCell><Badge variant="secondary">{l.source_page || "—"}</Badge></TableCell>
                    <TableCell className="text-muted-foreground text-xs">{fmtDate(l.created_at, true)}</TableCell>
                    <TableCell><Badge variant={STATUS_VARIANT[l.status || "ny"]}>{STATUS[l.status || "ny"]}</Badge></TableCell>
                    <TableCell className="text-right"><Button variant="outline" size="sm" onClick={() => openLead(l)} data-testid={`admin-lead-open-${l.id}`}>Åbn</Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Surface>

      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent data-testid="admin-lead-dialog">
          <DialogHeader><DialogTitle>{open?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-1 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div><p className="text-xs text-muted-foreground">E-mail</p><a href={`mailto:${open?.email}`} className="hover:underline">{open?.email}</a></div>
              <div><p className="text-xs text-muted-foreground">Telefon</p><p>{open?.phone || "—"}</p></div>
              <div><p className="text-xs text-muted-foreground">Kilde</p><p>{open?.source_page || "—"}</p></div>
              <div><p className="text-xs text-muted-foreground">Oprettet</p><p>{fmtDate(open?.created_at, true)}</p></div>
            </div>
            <div><p className="text-xs text-muted-foreground mb-1.5">Status</p>
              <Select value={status} onValueChange={setStatus}><SelectTrigger data-testid="lead-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div>
            <div><p className="text-xs text-muted-foreground mb-1.5">Intern note</p>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Kort intern note..." data-testid="lead-note" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(null)}>Luk</Button>
            <Button onClick={save} disabled={saving} data-testid="lead-save" className="gap-2">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Gem</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
